import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { AbandonChangesDialog } from '../../../shared/components/abandon-changes-dialog';
import { useAbandonChangesGuard } from '../../../shared/hooks/useAbandonChangesGuard';
import { extractDataBuilderErrorMessage } from '../../DataBuilder/api/data-builder-error-message';
import { DataBuilderSettingsForm } from './data-builder-settings-form';
import { getSettingsValidationErrors } from './data-builder-settings-validation';
import type { SettingsValues } from './helpers/settings-value';
import type { UseDataBuilderSettingsResult } from './use-data-builder-settings';

/**
 * Edits the schema-driven Data Builder settings in a modal that owns its own
 * draft (seeded from the committed values on open) and saves independently of
 * the surrounding client form. Cancel discards the draft; Save commits it.
 */
export const DataBuilderSettingsModal = ({
  isOpen,
  onClose,
  dbSettings,
  clientName,
  readonly,
}: {
  isOpen: boolean;
  onClose: () => void;
  dbSettings: UseDataBuilderSettingsResult;
  clientName: string | null | undefined;
  readonly?: boolean;
}) => {
  const toast = useToast();
  const { values, groups, isLoading, isError, isSaving, saveSettings } =
    dbSettings;

  const [draft, setDraft] = useState<SettingsValues>(values);
  const wasOpenRef = useRef(false);

  const isDirty = !isEqual(draft, values);
  const validationErrors = getSettingsValidationErrors(groups, draft);
  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  const {
    requestClose,
    isConfirmOpen,
    confirmAbandon,
    cancelAbandon,
    cancelRef,
  } = useAbandonChangesGuard({
    isDirty,
    onClose,
  });

  // Seed the draft from the committed values when the modal opens (false→true),
  // and once more if it opened before the schema finished loading (empty draft +
  // values just arrived). Never re-seed mid-edit, so a background refetch can't
  // clobber in-progress changes.
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    const openedEmpty =
      isOpen &&
      Object.keys(draft).length === 0 &&
      Object.keys(values).length > 0;
    if (justOpened || openedEmpty) {
      setDraft(values);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, values, draft]);

  const onSave = async () => {
    try {
      await saveSettings(clientName ?? '', draft);
      toast({
        title: 'Data Builder settings saved',
        status: 'success',
        duration: 4000,
        position: 'top-right',
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Data Builder settings not saved',
        description: extractDataBuilderErrorMessage(
          error,
          'Something went wrong saving the settings. Please try again.'
        ),
        status: 'error',
        duration: 5000,
        position: 'top-right',
      });
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={requestClose}
        size="xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxHeight="85vh" margin="5">
          <ModalHeader>
            <Text fontSize="lg" fontWeight="semibold">
              Data Builder Configuration
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <DataBuilderSettingsForm
              groups={groups}
              values={draft}
              setValue={(key, value) =>
                setDraft((prev) => ({ ...prev, [key]: value }))
              }
              isLoading={isLoading}
              isError={isError}
              errors={validationErrors}
              readonly={readonly}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={requestClose}>
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={onSave}
              isLoading={isSaving}
              isDisabled={
                readonly ||
                isLoading ||
                isError ||
                hasValidationErrors ||
                isSaving
              }
              data-testid="data-builder-settings-save"
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AbandonChangesDialog
        isOpen={isConfirmOpen}
        onConfirm={confirmAbandon}
        onCancel={cancelAbandon}
        cancelRef={cancelRef}
        body="You have unsaved Data Builder configuration changes. Are you sure you want to abandon them?"
      />
    </>
  );
};
